RivalIQ.module('graph', function (graph, App, Backbone, Marionette, $, _, moment, d3) {

    'use strict';

    const BottomLegendMarginTop = 20;
    const BottomLegendInterItemSpacing = 15;
    const BottomIntraItemSpacing = 5;
    const BottomLengthLineWidth = 25;
    const BottomLegendHeight = 25;
    const WarningTriaglePadding = 5;
    const YAxisTitleHeight = 20;
    const YAxisTitlePadding = 15;
    const SideLegendLeftPadding = 20;

    TimeSeries.prototype = new graph.Graph();

    function TimeSeries() {
        this.base = graph.Graph;
        this.base();
        this.tickSize = 'day';
        _.extend(this, Backbone.Events);
    }

    graph.TimeSeries = TimeSeries;

    TimeSeries.prototype.setHideActions = function (hideActions) {
        this.hideActions = hideActions;
    };

    TimeSeries.prototype.setHideExcluded = function (hideExcluded) {
        this.hideExcluded = hideExcluded;
    };

    TimeSeries.prototype.setOmitTitle = function (omitTitle) {
        this.omitTitle = omitTitle;
    };

    TimeSeries.prototype.setOmitLegend = function (omitLegend) {
        this.omitLegend = omitLegend;
    };

    TimeSeries.prototype.setShowBottomLegend = function (showBottomLegend) {
        this.showBottomLegend = showBottomLegend;
    };

    TimeSeries.prototype.setLimit = function (limit) {
        this.limit = limit;
    };

    TimeSeries.prototype.setTickSize = function (tickSize) {
        this.tickSize = tickSize;
    };

    TimeSeries.prototype.setFocusCompanyId = function (focusCompanyId) {
        this.focusCompanyId = focusCompanyId;
    };

    TimeSeries.prototype.setFocusCompanyOnly = function (focusCompanyOnly) {
        this.focusCompanyOnly = focusCompanyOnly;
    };

    TimeSeries.prototype.getColors = function (data) {
        const me = this;

        if (!me.color) {
            const color = d3.scale.category20();
            color.domain(_.sortBy(_.map(data, 'id'), _.identity));

            me.color = function (companyId) {
                if (companyId === me.focusCompanyId) {
                    return RivalIQ.enums.colors.focus;
                }

                if (companyId === 'benchmarkLine') {
                    return RivalIQ.enums.colors.competitor;
                }

                return color(companyId);
            };
        }

        return me.color;
    };

    TimeSeries.prototype.isReportSlide = function () {
        return $('.x-slide').length > 0;
    };

    /**
     * Gets elided data given a limit, and optional inclusions.
     * @param {Array} data The set of items to elide.
     * @param {Number} limit The maximum length expected after elision.
     * @param {Number[]} [inclusions] An optional array of ids that should be included regardless.
     * @return {Array} An elided set of data.
     */
    TimeSeries.prototype.getElidedData = function (data, limit, inclusions) {

        if (limit >= data.length) {

            return data;
        }

        const sorted = _.take(
            _.sortBy(data, function (datum) {

                return -_.get(datum, 'maxValue', 0);
            }),
            limit
        );

        const addendum = _.reduce(inclusions, function (memo, inclusion) {

            const existing = _.find(data, {
                id: inclusion
            });
            const candidate = _.find(sorted, {
                id: inclusion
            });
            return (!candidate && existing ? memo.concat(existing) : memo);
        }, []);

        return _.take(sorted, _.size(sorted) - _.size(addendum)).concat(addendum);
    };

    /**
     * Gets a custom axis tick function that will generate ticks every n days.
     * @param n
     * @returns {Function}
     */
    TimeSeries.prototype.getTicksEveryNDays = function (n) {

        // NOTE we need to use a custom tick function because were we to use d3.time.days
        // with a value of n the behavior is every n day by month, rather than
        // by year (which is what we need)
        return function (start, stop) {

            return d3.time.days.utc(start, stop).filter(function (d) {

                return (d3.time.dayOfYear(d) % n === 0);
            });
        };
    };

    TimeSeries.prototype.onAttachLoaded = function () {
        const me = this;
        me.adjustLabels();
    };

    TimeSeries.prototype.adjustLabels = function () {
        const me = this;
        d3.select(me.target).selectAll('.y-axis-label')
            .call(me.wrap, 200);
    };

    TimeSeries.prototype.getIntervalText = function (date) {
        const me = this;
        if (me.tickSize === 'month') {

            return moment.utc(date).format('MMMM');
        } else if (me.tickSize === 'week') {

            const startDate = moment.utc(date);
            const endDate = moment.utc(date).endOf('week');
            return `${startDate.format('ll')} - ${endDate.format('ll')}`;
        } else {

            if (me.timePeriod && me.timePeriod.mainPeriodDuration >= 720) {
                return moment.utc(date).format('MM/DD/YY');
            }

            return moment.utc(date).format('ll');
        }
    };

    TimeSeries.prototype.renderYAxis = function (svg, renderedCompanies, graphHeight, yAxisXPosition) {
        const me = this;

        // we need to get the max beforehand so that we can tweak it a bit if the
        // data is all zero, or the companies do not have the channel
        const yMax = d3.max(renderedCompanies, function (c) {
            return c.maxValue;
        });

        // we need to get the min beforehand so that we can tweak it a bit if the
        // max is the same as the min
        const yMin = d3.min(renderedCompanies, function (c) {
            return d3.min(c.values, function (v) {
                return v.total;
            });
        });

        const yTicks = me.getYTicks(yMin, yMax);
        // Yes the yAxis is getting the formatX formatter. This is b/c formatX
        // refers to the x value, not the x axis
        const yAxisFormat = me.yAxisFormat || me.formatX;
        const y = d3.scale.linear()
            .domain([yTicks.min, yTicks.max])
            .range([graphHeight, 0])
            .nice(yTicks.count);

        const yAxis = d3.svg.axis()
            .scale(y)
            .orient('left')
            .tickFormat(yAxisFormat);

        // explicitly set the number of yTicks since at numbers less than CUTOFF(10), we start to get non-integer vals.
        // note: we need to test yMax because it can be undefined
        if (yTicks.count) {
            yAxis.ticks(yTicks.count);
        }

        const yAxisContainer = svg.append('g')
            .attr('class', 'y axis')
            .call(yAxis);

        // This will house our y-axis title position; we'll try to set it based on the width of the yAxis, falling back
        // to a constant.
        const yAxisGElem = _.get(yAxisContainer, [0, 0]);
        let yAxisWidth;
        if (yAxisGElem && yAxisGElem.getBBox) {
            const yAxisBBox = yAxisGElem.getBBox();
            yAxisXPosition += yAxisBBox.width;
            yAxisWidth = yAxisBBox.width;
        } else {
            // we need to slightly amplify the effect when exporting a slide
            const positionMultiplier = me.isReportSlide() ? 1.75 : 1;

            // Use a static label position for now, since y axis labels should
            // never exceed 5 characters (xxx.xx)
            yAxisWidth = (5 * positionMultiplier);
        }

        if (yAxisContainer.attr) {
            yAxisContainer.attr('transform', `translate(${yAxisXPosition},0)`);
        }

        return {
            y: y,
            yMin: yMin,
            yMax: yMax,
            yAxisContainer: yAxisContainer,
            yAxisWidth: yAxisWidth,
            yAxisXPosition: yAxisXPosition
        };
    };

    TimeSeries.prototype.renderXAxis = function (svg, graphZeroXPosition, graphWidth, xAxisHeightOverride) {
        const me = this;

        const x = d3.time.scale.utc().range([graphZeroXPosition, graphWidth]);

        // pad with 1/2 tickSize on either end so that at small time ranges (e.g. 7 days), the graph doesn't sit on the y axis
        // In the day case, endHours gets a negative end padding since our day range overflows by 1 anyway
        // (i.e. a single day date range has a start and end almost 24 hours apart)
        let startHours;
        let endHours;
        switch (this.tickSize) {
            case 'day':
                startHours = (1 * 24) / 2;
                endHours = 0 - startHours;
                break;
            case 'week':
                startHours = endHours = (7 * 24) / 2;
                break;
            case 'month':
                startHours = endHours = (30 * 24) / 2;
                break;
            default:
                throw new Error(`unknown tickSize ${this.tickSize}`);
        }
        x.domain([
            moment.utc(this.dataDateRange.mainPeriodStart).subtract(startHours, 'hours').toDate(),
            moment.utc(this.dataDateRange.mainPeriodEnd).add(endHours, 'hours').toDate()
        ]);

        const xAxis = d3.svg.axis().scale(x).orient('bottom').tickFormat(function (d) {
            if (me.tickSize === 'month') {
                return moment.utc(d).format('MMM');
            } else if (me.tickSize === 'week') {
                return `${moment.utc(d).format('MM/DD')} - ${moment.utc(d).endOf('week').format('MM/DD')}`;
            } else {
                if (me.timePeriod && me.timePeriod.mainPeriodDuration >= 720) {
                    return moment.utc(d).format('MM/DD/YY');
                }

                return moment.utc(d).format('MM/DD');
            }
        });

        // this is where we decide which time scale to utilize based on time frame
        if (this.tickSize === 'week') {
            if (this.timePeriod.mainPeriodDuration < 180) {
                xAxis.ticks(d3.time.weeks.utc, 1);
            } else if (this.timePeriod.mainPeriodDuration < 360) {
                xAxis.ticks(d3.time.weeks.utc, 2);
            } else {
                xAxis.ticks(d3.time.weeks.utc, 4);
            }
        } else if (this.tickSize === 'month') {

            if (this.timePeriod.mainPeriodDuration < 720) {
                xAxis.ticks(d3.time.month.utc, 1);
            } else {
                xAxis.ticks(d3.time.month.utc, 2);
            }
        } else if (this.timePeriod.mainPeriodDuration <= 10) {
            xAxis.ticks(d3.time.days.utc, 1);
        } else if (this.timePeriod.mainPeriodDuration <= 20) {
            xAxis.ticks(me.getTicksEveryNDays(2));
        } else if (this.timePeriod.mainPeriodDuration < 90) {
            xAxis.ticks(me.getTicksEveryNDays(3));
        } else if (this.timePeriod.mainPeriodDuration < 180) {
            xAxis.ticks(d3.time.weeks.utc, 1);
        } else if (this.timePeriod.mainPeriodDuration < 360) {
            xAxis.ticks(d3.time.weeks.utc, 2);
        } else if (this.timePeriod.mainPeriodDuration < 720) {
            xAxis.ticks(d3.time.month.utc, 1);
        } else {
            xAxis.ticks(d3.time.month.utc, 2);
        }

        const xAxisElement = svg.append('g')
            .attr('class', 'x axis');

        xAxisElement
            .call(xAxis)
            .selectAll('text')
            .style('text-anchor', 'end')
            .attr('dx', '-.8em')
            .attr('dy', '.15em')
            .attr('transform', function () {
                return 'rotate(-65)';
            });

        if (_.isUndefined(xAxisHeightOverride)) {
            const xAxisBBox = _.result(xAxisElement, ['0', '0', 'getBBox']);
            xAxisHeightOverride = xAxisBBox.height;
        }

        // now that we know how tall the x-axis is, we can place the axis and give the rest of the height to the graph.
        let graphHeight = me.height - me.margin.top - xAxisHeightOverride - me.margin.bottom;

        if (me.showBottomLegend && !me.omitLegend) {
            graphHeight -= BottomLegendHeight;
        }

        const lineChartBottom = graphHeight + xAxisHeightOverride;
        xAxisElement.attr('transform', `translate(0,${graphHeight})`);

        return {
            x: x,
            xAxis: xAxis,
            xAxisElement: xAxisElement,
            lineChartBottom: lineChartBottom,
            graphHeight: graphHeight,
            xAxisHeight: xAxisHeightOverride
        };
    };

    TimeSeries.prototype.makeSvgMeasurements = function (renderedCompanies, companies) {
        const me = this;

        let graphWidth = me.width - me.margin.right - me.margin.left;

        const measurementSvg = d3.select(this.target).append('svg');
        const yAxisXPosition = (me.omitTitle ? 0 : YAxisTitleHeight + YAxisTitlePadding);

        // This isn't likely going to be the real height, but we need a placeholder to see how wide that axis is going to be.
        const placeholderHeight = me.height - me.margin.top - me.margin.bottom;
        const graphZeroXPosition = me.renderYAxis(measurementSvg, renderedCompanies, placeholderHeight, yAxisXPosition).yAxisXPosition;

        if (!me.omitLegend && !me.showBottomLegend) {
            const legendBBox = me.renderSideLegend(measurementSvg, companies, renderedCompanies, _.noop, _.noop);
            graphWidth -= legendBBox.width + SideLegendLeftPadding;
        }

        const xAxisMeasurements = me.renderXAxis(measurementSvg, graphZeroXPosition, graphWidth);

        measurementSvg.remove();

        return _.extend(xAxisMeasurements, {
            graphZeroXPosition: graphZeroXPosition,
            graphWidth: graphWidth
        });
    };

    TimeSeries.prototype.render = function () {
        const me = this;

        $(this.target).empty();

        const data = me.getElidedData(me.data, me.limit, [me.focusCompanyId]);
        let companies = _.map(data, function (datum) {
            return _.extend({}, _.omit(datum, 'values'), {
                values: _.map(datum.values, function (d) {
                    return {
                        id: datum.id,
                        name: datum.name,
                        date: new Date(d.date),
                        total: d.total,
                        company: datum
                    };
                })
            });
        });

        if (me.focusCompanyOnly) {
            companies = _.filter(companies, {
                id: me.focusCompanyId
            });
        }

        const renderedCompanies = _.filter(companies, {
            isExcluded: false
        });

        // We do a two pass rendering - first a minimal rendering to figure out how much space we have given the data and styles,
        // then again to actually render onto the screen. We do two passes because rendering tons of points can be expensive if the
        // SVG is in the dom, but we need the SVG to be in the dom to calculate sizes.
        const measurements = me.makeSvgMeasurements(renderedCompanies, companies);

        const toggleVisible = function (d, i, shouldNotRender) {

            const id = d.id;
            const datum = _.find(data, {
                id: id
            });

            if (datum) {

                datum.isExcluded = !datum.isExcluded;
                me.trigger(`exclusion:${datum.isExcluded ? 'added' : 'removed'}`, id);
            }

            if (shouldNotRender !== true) {

                me.render();
                me.adjustLabels();
            }
        };

        // we have to do this rigmarole to preserve initial company colors, if we
        // didn't then each time the graph was rendered the colors would be re-assigned
        // note too that ordering here is imporant, as we are about to filter companies
        // inside and out below
        const color = me.getColors(me.data);

        // We create the SVG el here, but we don't throw it onto the dom until the every end. Otherwise, the browser
        // would have to recalculate styles whenever we appended svg elements.
        const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        const svg = d3.select(svgEl)
            .attr('width', me.width)
            .attr('height', me.height)
            .append('g')
            .attr('transform', `translate(${me.margin.left},${me.margin.top})`);

        const yAxisInfo = me.renderYAxis(svg, renderedCompanies, measurements.graphHeight, measurements.graphZeroXPosition);
        const y = yAxisInfo.y;
        const yMin = yAxisInfo.yMin;

        // Render the horizontal bar lines
        svg.selectAll('.tick line')
            .filter(Number)
            .attr('x1', measurements.graphWidth - measurements.graphZeroXPosition);

        const xAxisInfo = me.renderXAxis(svg, measurements.graphZeroXPosition, measurements.graphWidth, measurements.xAxisHeight);
        const x = xAxisInfo.x;
        const xAxis = xAxisInfo.xAxis;

        let tooltip = d3.select('div.tooltip');

        if (!$('div.tooltip').length) {
            tooltip = d3.select('body').append('div')
                .attr('class', 'tooltip x-graph-tooltip hide')
                .style('opacity', 0);
        }

        const line = d3.svg.line()
            .defined(function (d) {
                return d.total !== null;
            })
            .x(function (d) {
                return x(d.date);
            })
            .y(function (d) {
                return y(d.total);
            });

        const company = svg.selectAll('.x-company')
            .data(renderedCompanies)
            .enter().append('g')
            .attr('class', '.x-company');

        company.append('path')
            .attr('class', function (d) {
                let cls = 'line x-history-path';

                if (d.focus) {
                    cls += ' x-focus';
                }

                if (d.lineClass) {
                    cls += ` ${d.lineClass}`;
                }

                return cls;
            })
            .attr('d', function (d) {
                return line(d.values);
            })
            .style('stroke', function (d) {
                return color(d.id);
            });

        // this is where we add the individual data points to the line
        const point = company.append('g')
            .attr('class', 'line-point');

        const hideTooltip = function () {
            const timeout = setTimeout(function () {
                tooltip.transition().duration(500).style('opacity', 0);
                tooltip.attr('class', 'tooltip x-graph-tooltip hide');
            }, 300);

            // this is how we enable sticky tooltips that don't disappear if you
            // mouseover them, and do disappear once you leave
            $(tooltip[0])
                .off('mouseenter')
                .off('mouseleave')
                .one('mouseenter', function () {
                    clearTimeout(timeout);
                })
                .one('mouseleave', hideTooltip);
        };

        point.selectAll('circle')
            .data(function (d) {
                return d.values;
            })
            .enter().append('circle')
            .attr('cx', function (d) {
                return x(d.date);
            })
            .attr('cy', function (d) {
                return y(d.total);
            })
            .attr('r', me.markerSize)
            .attr('class', function (d) {
                return `x-history-dot ${d.total === null ? 'hide' : ''}`;
            })
            .attr('stroke', function (d) {
                return color(d.id);
            })
            .on('mouseover', function (d, i) {

                const tooltipTemplate = Marionette.TemplateCache.get('#metricChartTooltip');

                d3.select('#tooltipContainer').remove();

                tooltip = d3.select('body')
                    .append('div')
                    .attr('id', 'tooltipContainer')
                    .attr('class', 'popover fade bottom in')
                    .style('display', 'block');
                const content = tooltip.append('div');

                const tooltipContentData = {
                    className: null,
                    name: me.getIntervalText(d.date),
                    metricName: d.name,
                    value: me.formatForPopover(d.total),
                    percentage: null,
                    showSubmetric: true,
                    color: color(d.id),
                    isContentLinkable: me.isContentLinkable,
                    submetricData: _.get(me.tooltipSubmetrics, [d.id, i])
                };

                content.html(tooltipTemplate(tooltipContentData));

                const width = parseInt(tooltip.style('width'));
                tooltip
                    .style('left', `${d3.event.pageX + 5 - width / 2}px`)
                    .style('top', `${d3.event.pageY}px`);

                // if this is something that can link to content then we need to
                // add a click listener so that we can properly instantiate the
                // modal content view
                if (me.isContentLinkable) {
                    $(tooltip[0]).find('.x-view-content').off('click').one('click', function () {
                        // hour, minute, second, millisecond matters
                        // we need to make sure that our time period starts beginning of day (00:00:00.000)
                        // and ends end of day (23:59:59.999), all in UTC
                        me.trigger('analyze-content', d, {
                            mainPeriodStart: moment.utc(d.date).startOf(me.tickSize),
                            mainPeriodEnd: moment.utc(d.date).endOf(me.tickSize)
                        });
                        return false;
                    });
                }

            }).on('mouseout', hideTooltip);


        // If this graph goes below 0 draw a line at 0
        if (yMin < 0) {
            svg.append('g')
                .attr('class', 'zero-axis axis')
                .attr('transform', `translate(0,${y(0)})`)
                .call(xAxis.ticks(0).tickFormat(''));
        }

        // Now that we've gotten the expensive point dom elements created, let's add to the dom. Do it before rendering the legend and y-axis,
        // as those depend on bbox which will only work once we've added the svg to the dom.
        (this.target).appendChild(svgEl);

        if (!me.omitTitle) {
            const yAxisLabelXPosition = YAxisTitleHeight / 2;
            svg
                .append('text')
                .attr('class', 'y-axis-label')
                .attr('transform', 'rotate(-90)')
                .attr('y', yAxisLabelXPosition)
                .attr('x', -measurements.graphHeight / 2)
                .attr('dy', '.35em')
                .style('text-anchor', 'middle')
                .text(me.ylabel);
        }

        if (!me.omitLegend) {
            if (me.showBottomLegend) {
                me.renderBottomLegend(svg, measurements.xAxisElement, measurements.lineChartBottom, companies, renderedCompanies, toggleVisible, color);
            } else {
                me.renderSideLegend(svg, companies, renderedCompanies, toggleVisible, color);
            }
        }
    };

    TimeSeries.prototype.renderBottomLegend = function (svg, xAxis, lineChartBottom, companies, renderedCompanies, toggleVisible, color) {
        const me = this;

        const legend = svg.append('g')
            .attr('class', 'legend')
            .attr('height', 100)
            .attr('width', 100);

        let lastX = 0;
        _.each(companies, function (company, companyIdx) {
            lastX += BottomLengthLineWidth + BottomIntraItemSpacing;
            const companyLegendItem = legend.append('text')
                .attr('class', function () {

                    let className = 'x-legend-company pointer';

                    if (company.isExcluded) {

                        className += ` ${graph.Graph.EXCLUDED_LEGEND_CLASS_NAME}`;
                    }

                    return className;
                })
                .attr('x', function () {
                    return lastX;
                })
                .attr('y', function () {
                    return 0;
                })
                .attr('title', function () {
                    return company.name;
                })
                .text(function () {
                    const length = 19;
                    return me.trimOrdinalLabel(company.name, length);
                })
                .on('click', function () {
                    toggleVisible(company, companyIdx, false);
                });

            const legendItemBBox = companyLegendItem.node().getBBox();
            const lineStart = lastX - (BottomLengthLineWidth + BottomIntraItemSpacing);
            const lineY = -legendItemBBox.height / 4;

            legend.append('rect')
                .attr('class', function () {
                    let cls = 'legend-line-click-handler';
                    if (company.isExcluded) {
                        cls += ` ${graph.Graph.EXCLUDED_LEGEND_CLASS_NAME}`;
                    }

                    return cls;
                })
                .attr('x', lineStart)
                .attr('width', BottomLengthLineWidth + BottomIntraItemSpacing)
                .attr('y', -10)
                .attr('height', 12)
                .on('click', function () {
                    toggleVisible(company, companyIdx, false);
                });

            legend.append('line')
                .attr('class', function () {
                    let cls = 'line x-history-path';

                    if (company.focus) {
                        cls += ' x-focus';
                    }

                    if (company.lineClass) {
                        cls += ` ${company.lineClass}`;
                    }

                    if (company.isExcluded) {
                        cls += ` ${graph.Graph.EXCLUDED_LEGEND_CLASS_NAME}`;
                    }

                    return cls;
                })
                .attr('x1', lineStart)
                .attr('x2', lineStart + BottomLengthLineWidth)
                .attr('y1', lineY)
                .attr('y2', lineY)
                .style('stroke', function () {
                    return color(company.id);
                });

            if (company.notAvailableTooltip) {
                const triangle = legend.append('text')
                    .attr('font-family', 'FontAwesome')
                    .attr('class', 'x-not-enough-history-icon')
                    .attr('height', 10)
                    .attr('width', 10)
                    .attr('y', 0)
                    .attr('x', lastX + legendItemBBox.width + WarningTriaglePadding)
                    .text('\uf071');

                App.utils.tooltip.addPopover($(triangle.node()), company.notAvailableTooltip, {
                    placement: 'top'
                });

                lastX += triangle.node().getBBox().width + WarningTriaglePadding;
            }

            lastX += legendItemBBox.width + BottomLegendInterItemSpacing;
        });


        const legendBBox = legend.node().getBBox();
        const legendLocationX = me.width - legendBBox.width - me.margin.left; // right aligned with the whole chart.
        const legendLocationY = lineChartBottom + BottomLegendMarginTop;
        legend.attr('transform', `translate(${legendLocationX},${legendLocationY})`);

    };

    TimeSeries.prototype.renderSideLegend = function (svg, companies, renderedCompanies, toggleVisible, color) {
        const me = this;

        // NOTE we can add the show none action when there are rendered companies
        const canShowNone = (renderedCompanies.length !== 0);
        // NOTE we can add the show all action when the rendered companies is
        // a subset of the total companies (after the limit culling, of course)
        const canShowAll = (renderedCompanies.length !== companies.length);

        const legend = svg.append('g')
            .attr('class', 'legend')
            .attr('height', 100)
            .attr('width', 100);

        // we need to sort the companies for the legend, and make sure that we do
        // so before creating aspects of the legend
        let sortedCompanies = _.sortBy(companies, function (company) {
            return company.name.toLowerCase();
        });

        if (me.hideExcluded) {
            sortedCompanies = _.filter(sortedCompanies, {
                isExcluded: false
            });
        }

        const legendRectWidth = me.isReportSlide() ? 15 : 10;

        const legendPadding = legendRectWidth / 2.5;

        legend.selectAll('rect')
            .data(sortedCompanies)
            .enter()
            .append('rect')
            .attr('x', 0)
            .attr('y', function (d, i) {
                let position = i * legendRectWidth;
                if (i > 0) {
                    position += (legendPadding * i);
                }
                return position;
            })
            .attr('width', legendRectWidth)
            .attr('height', legendRectWidth)
            .style('fill', function (d) {
                return color(d.id);
            })
            .attr('class', function (d) {

                let className = 'pointer';

                if (d.isExcluded) {

                    className += ` ${graph.Graph.EXCLUDED_LEGEND_CLASS_NAME}`;
                }

                return className;
            })
            .on('click', toggleVisible);

        let lastLegendOffset = 0;
        const companyWidth = 25;

        legend.selectAll('text')
            .data(sortedCompanies)
            .enter()
            .append('text')
            .attr('class', function (d) {

                let className = 'x-legend-company pointer';

                if (d.isExcluded) {

                    className += ` ${graph.Graph.EXCLUDED_LEGEND_CLASS_NAME}`;
                }

                return className;
            })
            .attr('x', function () {
                return legendRectWidth;
            })
            .attr('y', function (d, i) {
                let position = i * legendRectWidth - 1;
                if (i > 0) {
                    position += (legendPadding * i);
                }
                return (lastLegendOffset = position + legendRectWidth);
            })
            .attr('width', companyWidth)
            .attr('title', function (d) {
                return d.name;
            })
            .html(function (d) {
                const length = me.isReportSlide() ? 13 : 19;
                return `&nbsp;${me.trimOrdinalLabel(d.name, length)}`;
            })
            .on('click', toggleVisible);

        const toggleAll = function (expectation) {

            return function () {

                _.each(companies, function (company) {

                    if (company.isExcluded === expectation) {

                        toggleVisible(company, null, true);
                    }
                });

                me.render();
                me.adjustLabels();
            };
        };

        function addTextToChart(title, onClick) {
            legend.append('text')
                .attr('class', 'x-legend-action pointer x-graph-link x-export-hide')
                .attr('title', title)
                .attr('x', legendPadding + legendRectWidth)
                .attr('y', lastLegendOffset + (legendRectWidth * 1.5))
                .attr('width', companyWidth)
                .text(title)
                .on('click', onClick);

            lastLegendOffset += legendRectWidth * 1.5;
        }

        if (!me.hideActions) {

            if (canShowAll) {

                addTextToChart('Show All', toggleAll(true));
            }

            if (canShowNone) {

                addTextToChart('Show None', toggleAll(false));
            }
        }

        // Now that we've populated the legend, we can measure it and right align it.
        const legendBBox = legend.node().getBBox();
        const legendLocationX = me.width - legendBBox.width - me.margin.left;
        legend.attr('transform', `translate(${legendLocationX},${0})`);

        return legendBBox;
    };

    TimeSeries.prototype.getYTicks = function (yMin, yMax) {
        const me = this;

        // TODO get rid of this fake metric and use a real one
        const metric = {
            getType: _.constant(me.dataType),
            getTimeAnalysisMode: _.constant(me.timeAnalysisMode),
            getMetricType: function () {
                if (me.isVolumetric) {
                    return RivalIQ.enums.metricType.VOLUME;
                }
            }
        };

        const ticks = 5;
        const domain = [yMin, yMax];
        const paddedYDomain = RivalIQ.graph.utils.getPaddedYDomain(metric, domain, ticks);

        return {
            count: ticks,
            min: _.head(paddedYDomain),
            max: _.last(paddedYDomain)
        };
    };

}, window.moment, window.d3);
